import { useState, useCallback } from "react";
import { Button } from "@cieda/djayma-ui";
import { Send, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useYangoDispatch } from "@/hooks/admin/useYango";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface YangoDispatchButtonProps {
  readonly orderId: string;
  readonly orderRef: string;
  readonly disabled?: boolean;
  readonly onSuccess?: () => void;
  readonly onError?: (error: Error) => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function YangoDispatchButton({
  orderId,
  orderRef,
  disabled = false,
  onSuccess,
  onError,
}: YangoDispatchButtonProps) {
  const { t } = useTranslation("logistics");
  const [showConfirm, setShowConfirm] = useState(false);
  const dispatch = useYangoDispatch();

  const handleDispatch = useCallback(() => {
    dispatch.mutate(
      { orderId },
      {
        onSuccess: () => {
          setShowConfirm(false);
          onSuccess?.();
        },
        onError: (error) => {
          setShowConfirm(false);
          onError?.(error);
        },
      },
    );
  }, [orderId, dispatch, onSuccess, onError]);

  if (showConfirm) {
    return (
      <div className="flex items-center gap-2">
        <span className="text-xs text-stone-500">
          {t("yango.confirm_dispatch", { ref: orderRef })}
        </span>
        <Button
          size="sm"
          onClick={handleDispatch}
          disabled={dispatch.isPending}
          className="gap-1.5 bg-amber-600 hover:bg-amber-700"
        >
          {dispatch.isPending ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Send className="h-3.5 w-3.5" />
          )}
          {t("yango.confirm")}
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setShowConfirm(false)}
          disabled={dispatch.isPending}
        >
          {t("yango.cancel_action")}
        </Button>
      </div>
    );
  }

  return (
    <Button
      size="sm"
      variant="outline"
      onClick={() => setShowConfirm(true)}
      disabled={disabled || dispatch.isPending}
      className="gap-1.5"
    >
      <Send className="h-3.5 w-3.5" />
      {t("yango.dispatch")}
    </Button>
  );
}
